import React, { useState } from 'react';
import { SectionTitle, LuxuryTable } from '@/components/Luxury';
import { Shield, Pencil, Check, X, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import type { UnitCountReconciliation } from '../../types/calculations';

type ConfidenceLevel = 'HIGH' | 'MEDIUM' | 'LOW';

interface DataQualityCardProps {
  unitCountConfidence: ConfidenceLevel;
  unitCountSource: string;
  unitCountValue: number | string;
  currentRentConfidence: ConfidenceLevel;
  currentRentSource: string;
  currentRentValue: number | string;
  marketRentConfidence: ConfidenceLevel;
  marketRentSource: string;
  marketRentValue: number | string;
  capexBudgetConfidence: ConfidenceLevel;
  capexBudgetSource: string;
  capexBudgetValue: number | string;
  unitCountReconciliation?: UnitCountReconciliation;
  propertyId?: string;
}

const confidenceBadgeStyles: Record<ConfidenceLevel, string> = {
  HIGH: 'bg-emerald text-white',
  MEDIUM: 'bg-amber-500 text-white',
  LOW: 'bg-crimson text-white',
};

interface ConfidenceBadgeProps {
  level: ConfidenceLevel;
}

const ConfidenceBadge: React.FC<ConfidenceBadgeProps> = ({ level }) => {
  return (
    <span
      className={`
        inline-flex items-center px-2 py-0.5 rounded text-sm font-semibold uppercase tracking-wider
        ${confidenceBadgeStyles[level]}
      `}
    >
      {level}
    </span>
  );
};

interface DataRow {
  field: string;
  value: number | string;
  confidence: ConfidenceLevel;
  source: string;
}

export const DataQualityCard: React.FC<DataQualityCardProps> = ({
  unitCountConfidence,
  unitCountSource,
  unitCountValue,
  currentRentConfidence,
  currentRentSource,
  currentRentValue,
  marketRentConfidence,
  marketRentSource,
  marketRentValue,
  capexBudgetConfidence,
  capexBudgetSource,
  capexBudgetValue,
  unitCountReconciliation,
  propertyId,
}) => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<number>(Number(unitCountValue) || 1);
  const [isSaving, setIsSaving] = useState(false);
  const [showSources, setShowSources] = useState(false);

  const handleOverride = async () => {
    if (!propertyId || editValue < 1 || editValue > 200) return;
    setIsSaving(true);
    try {
      await api.patch(`/v2/properties/${propertyId}/unit-count`, {
        unit_count: editValue,
      });
      // Invalidate underwriting queries to refetch with new unit count
      queryClient.invalidateQueries({ queryKey: ['underwriting', propertyId] });
      setIsEditing(false);
    } catch (err) {
      console.error('Unit count override failed:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const dataRows: DataRow[] = [
    {
      field: 'Unit Count',
      value: unitCountValue,
      confidence: unitCountConfidence,
      source: unitCountSource,
    },
    {
      field: 'Current Rent',
      value: currentRentValue,
      confidence: currentRentConfidence,
      source: currentRentSource,
    },
    {
      field: 'Market Rent',
      value: marketRentValue,
      confidence: marketRentConfidence,
      source: marketRentSource,
    },
    {
      field: 'CapEx Budget',
      value: capexBudgetValue,
      confidence: capexBudgetConfidence,
      source: capexBudgetSource,
    },
  ];

  const reconciliation = unitCountReconciliation;
  const hasConflict = reconciliation?.conflict_detected ?? false;
  const sourcesChecked = reconciliation?.sources_checked ?? [];

  return (
    <div className="bg-graphite rounded-lg p-6 border border-slate-dark">
      <SectionTitle icon={<Shield className="w-5 h-5" />}>
        Data Quality & Confidence
      </SectionTitle>

      {/* Unit count conflict warning */}
      {hasConflict && reconciliation?.conflict_details && (
        <div className="mb-4 flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2">
          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-sm font-semibold text-amber-400 uppercase tracking-wider">
              Unit Count Conflict
            </span>
            <p className="text-sm text-stone-700 mt-0.5">{reconciliation.conflict_details}</p>
          </div>
        </div>
      )}

      <LuxuryTable>
        <thead>
          <tr>
            <th>Data Point</th>
            <th>Value</th>
            <th>Confidence</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, index) => (
            <tr key={index}>
              <td className="font-medium text-ivory">
                {row.field}
                {row.field === 'Unit Count' && propertyId && (
                  <button
                    onClick={() => {
                      setEditValue(Number(unitCountValue) || 1);
                      setIsEditing(!isEditing);
                    }}
                    className="ml-2 text-stone-700 hover:text-gold transition-colors inline-flex"
                    title="Override unit count"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
              </td>
              <td className="num">
                {row.field === 'Unit Count' && isEditing ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      min={1}
                      max={200}
                      value={editValue}
                      onChange={(e) => setEditValue(parseInt(e.target.value) || 1)}
                      className="w-16 bg-obsidian border border-slate-dark rounded px-2 py-0.5 text-ivory text-sm font-mono focus:border-gold focus:outline-none"
                      disabled={isSaving}
                    />
                    <button
                      onClick={handleOverride}
                      disabled={isSaving}
                      className="text-emerald-400 hover:text-emerald-300 disabled:opacity-50"
                      title="Confirm"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      disabled={isSaving}
                      className="text-red-400 hover:text-red-300 disabled:opacity-50"
                      title="Cancel"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  row.value
                )}
              </td>
              <td>
                <ConfidenceBadge level={row.confidence} />
              </td>
              <td className="text-stone-700">{row.source}</td>
            </tr>
          ))}
        </tbody>
      </LuxuryTable>

      {/* Source breakdown expandable section */}
      {sourcesChecked.length > 0 && (
        <div className="mt-3 border-t border-slate-dark pt-3">
          <button
            onClick={() => setShowSources(!showSources)}
            className="flex items-center gap-1.5 text-sm text-stone-700 hover:text-stone-700 transition-colors"
          >
            {showSources ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
            <span className="uppercase tracking-wider font-semibold">
              Unit Count Sources ({sourcesChecked.length})
            </span>
          </button>

          {showSources && (
            <div className="mt-2 space-y-1.5">
              {sourcesChecked.map((source, idx) => {
                const isWinner = source.source_name === reconciliation?.resolved_source;
                return (
                  <div
                    key={idx}
                    className={`flex items-center justify-between px-3 py-1.5 rounded text-sm ${
                      isWinner
                        ? 'bg-emerald-500/10 border border-emerald-500/20'
                        : 'bg-obsidian/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {isWinner && (
                        <Check className="w-3 h-3 text-emerald-400" />
                      )}
                      <span className={isWinner ? 'text-emerald-300 font-medium' : 'text-stone-700'}>
                        {source.source_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-ivory">{source.unit_count} units</span>
                      <ConfidenceBadge level={source.confidence.toUpperCase() as ConfidenceLevel} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
